/**
 * QuickCalculator Component - Currency, Weight, Distance converter
 */
import React, { useState } from 'react';

const QuickCalculator = ({ isDarkMode, currencyRates }) => {
  const [calcMode, setCalcMode] = useState('currency'); // 'currency' | 'weight' | 'distance'
  const [inputValue, setInputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('DH');
  
  const convertCurrency = (value, from) => {
    const numVal = parseFloat(value) || 0;
    if (from === 'DH') {
      return { dh: numVal, usd: (numVal * currencyRates.USD).toFixed(2), eur: (numVal * currencyRates.EUR).toFixed(2) };
    } else if (from === 'USD') {
      const dhVal = numVal / currencyRates.USD;
      return { dh: dhVal.toFixed(2), usd: numVal, eur: (dhVal * currencyRates.EUR).toFixed(2) };
    } else if (from === 'EUR') {
      const dhVal = numVal / currencyRates.EUR;
      return { dh: dhVal.toFixed(2), usd: (dhVal * currencyRates.USD).toFixed(2), eur: numVal };
    }
    return { dh: 0, usd: 0, eur: 0 };
  };
  
  const convertWeight = (value, from) => {
    const numVal = parseFloat(value) || 0;
    if (from === 'kg') return { kg: numVal, lbs: (numVal * 2.205).toFixed(2), g: (numVal * 1000).toFixed(0) };
    if (from === 'lbs') return { kg: (numVal / 2.205).toFixed(2), lbs: numVal, g: ((numVal / 2.205) * 1000).toFixed(0) };
    if (from === 'g') return { kg: (numVal / 1000).toFixed(3), lbs: ((numVal / 1000) * 2.205).toFixed(2), g: numVal };
    return { kg: 0, lbs: 0, g: 0 };
  };
  
  const convertDistance = (value, from) => {
    const numVal = parseFloat(value) || 0;
    if (from === 'km') return { km: numVal, m: (numVal * 1000).toFixed(0), mi: (numVal * 0.6214).toFixed(2) };
    if (from === 'm') return { km: (numVal / 1000).toFixed(3), m: numVal, mi: ((numVal / 1000) * 0.6214).toFixed(3) };
    if (from === 'mi') return { km: (numVal / 0.6214).toFixed(2), m: ((numVal / 0.6214) * 1000).toFixed(0), mi: numVal };
    if (from === 'inch') return { km: '-', m: (numVal * 0.0254).toFixed(3), mm: (numVal * 25.4).toFixed(1), inch: numVal };
    if (from === 'mm') return { km: '-', m: (numVal / 1000).toFixed(4), mm: numVal, inch: (numVal / 25.4).toFixed(2) };
    return { km: 0, m: 0, mi: 0 };
  };
  
  const getResults = () => {
    if (calcMode === 'currency') return convertCurrency(inputValue, fromUnit);
    if (calcMode === 'weight') return convertWeight(inputValue, fromUnit);
    return convertDistance(inputValue, fromUnit);
  };
  
  const results = getResults();
  
  return (
    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
      <div className="flex gap-1 mb-3">
        {[
          { id: 'currency', icon: '💱', label: 'Currency' },
          { id: 'weight', icon: '⚖️', label: 'Weight' },
          { id: 'distance', icon: '📏', label: 'Distance' }
        ].map(mode => (
          <button
            key={mode.id}
            onClick={() => { 
              setCalcMode(mode.id); 
              setFromUnit(mode.id === 'currency' ? 'DH' : mode.id === 'weight' ? 'kg' : 'km'); 
              setInputValue(''); 
            }}
            className={`flex-1 py-1.5 rounded text-xs font-medium ${
              calcMode === mode.id 
                ? 'bg-blue-500 text-white' 
                : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-white text-gray-600'
            }`}
          >
            {mode.icon} {mode.label}
          </button>
        ))}
      </div>
      
      <div className="flex gap-2 mb-3">
        <input
          type="number"
          placeholder="Enter value..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white border'}`}
        />
        <select
          value={fromUnit}
          onChange={(e) => setFromUnit(e.target.value)}
          className={`w-20 px-2 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white border'}`}
        >
          {calcMode === 'currency' && <>
            <option value="DH">DH</option>
            <option value="USD">$</option>
            <option value="EUR">€</option>
          </>}
          {calcMode === 'weight' && <>
            <option value="kg">kg</option>
            <option value="lbs">lbs</option>
            <option value="g">g</option>
          </>}
          {calcMode === 'distance' && <>
            <option value="km">km</option>
            <option value="m">m</option>
            <option value="mi">miles</option>
            <option value="inch">inch</option>
            <option value="mm">mm</option>
          </>}
        </select>
      </div>
      
      {inputValue && (
        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
          {calcMode === 'currency' && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🇲🇦 DH</p><p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{results.dh}</p></div>
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🇺🇸 USD</p><p className={`font-bold text-green-500`}>${results.usd}</p></div>
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>🇪🇺 EUR</p><p className={`font-bold text-blue-500`}>€{results.eur}</p></div>
            </div>
          )}
          {calcMode === 'weight' && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>kg</p><p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{results.kg}</p></div>
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>lbs</p><p className={`font-bold text-orange-500`}>{results.lbs}</p></div>
              <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>g</p><p className={`font-bold text-purple-500`}>{results.g}</p></div>
            </div>
          )}
          {calcMode === 'distance' && (
            <div className="grid grid-cols-3 gap-2 text-center">
              {(fromUnit === 'inch' || fromUnit === 'mm') ? (
                <>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>inch</p><p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{results.inch || '-'}</p></div>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>mm</p><p className={`font-bold text-blue-500`}>{results.mm || '-'}</p></div>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>m</p><p className={`font-bold text-green-500`}>{results.m}</p></div>
                </>
              ) : (
                <>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>km</p><p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{results.km}</p></div>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>m</p><p className={`font-bold text-green-500`}>{results.m}</p></div>
                  <div><p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>miles</p><p className={`font-bold text-orange-500`}>{results.mi}</p></div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickCalculator;
